import storage from '@/utils/storage';
import { Switch } from '@/components/ui/switch';
import { useMe } from '@/features/auth/api/get-me';
import { useSettingsStore } from '@/stores/settingsStore';
import { Combobox } from '@/components/ui/combobox';
import { SignalRContext } from '@/providers/SignalRProvider';
import { logout } from '@/features/auth/api/logout';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { getLabelValue } from '@/utils/getLabelValue';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { PrinterDropdown } from '@/features/desktop/components/printer-dropwdown';
import { useUserEvents } from '@/api/get-user-events';
import { Loader2 } from 'lucide-react';

export function Settings() {
  const enableReEntry = useSettingsStore(state => state.allowReEntry);
  const enableBookedOnly = useSettingsStore(state => state.onlyBooked);
  const extendedParticipation = useSettingsStore(state => state.extendedParticipation);
  const direction = useSettingsStore(state => state.direction);
  const currentEventId = useSettingsStore(state => state.eventId);
  const alwaysPrint = useSettingsStore(state => state.alwaysPrint);
  const deviceId = storage.getDeviceId();

  const setEnableReEntry = useSettingsStore(state => state.setAllowReEntry);
  const setEnableBookedOnly = useSettingsStore(state => state.setOnlyBooked);
  const setExtendedParticipation = useSettingsStore(state => state.setExtendedParticipation);
  const setDirection = useSettingsStore(state => state.setDirection);
  const setCurrentEventId = useSettingsStore(state => state.setEventId);
  const setBreakoutId = useSettingsStore(state => state.setBreakoutId);
  const setAlwaysPrint = useSettingsStore(state => state.setAlwaysPrint);

  const { data: user, isLoading: isLoadingMe } = useMe({
    config: {
      enabled: !!storage.getAccessToken(),
      refetchInterval: 1000 * 60 * 5, // 5 minutes,
    },
  });

  const { data: events = [], isLoading: isLoadingEvents } = useUserEvents({
    eventId: user?.defaultEventId ?? 0,
    config: {
      enabled: !!user?.defaultEventId,
    },
  });

  const handleEventChange = (eventId: number) => {
    setCurrentEventId(eventId);
    setBreakoutId(eventId);

    SignalRContext.invoke('onsiteHandshake', {
      userGuid: user?.userGuid,
      eventId,
      ConnectionId: '123',
    });
  };

  return (
    <div className="w-screen max-w-screen overflow-x-hidden">
      <div className="mx-auto w-full max-w-[100vw] overflow-hidden grid gap-4 px-3 sm:px-4 py-4 box-border">
        {/* Header */}
        <div className='flex gap-4'>
          <img src="/jirango.ico" alt="Logo" className="h-12 w-auto" />
          <div className="space-y-2">
            <h4 className="font-medium text-md leading-none">{getLabelValue('PassageSettings')}</h4>
            <p className="text-sm text-muted-foreground">{getLabelValue('SettingDescription')}</p>
          </div>
        </div>

        {/* Event Selection */}
        <div className="grid gap-2">
          <Label>{getLabelValue('ChooseMainEvent')}</Label>
          {isLoadingEvents ? (
            <div className="flex gap-2">
              {getLabelValue('Loading')}
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <Combobox
              className="bg-zinc-100"
              value={currentEventId}
              onChange={handleEventChange}
              options={events
                .filter(event => !event.parentEventId)
                ?.map(event => ({
                  prefix: event.eventId,
                  label: `${event.eventId} - ${event.name}`,
                  value: event.eventId,
                }))}
            />
          )}
        </div>

        {/* Printer Selection */}
        <div className="grid gap-2">
          <Label>{getLabelValue('ChoosePrinter')}</Label>
          <PrinterDropdown />
        </div>

        {/* Toggle Settings */}
        {[
          {
            checked: enableBookedOnly,
            onChange: () => setEnableBookedOnly(!enableBookedOnly),
            label: getLabelValue('OnlyBooked'),
            description: getLabelValue('OnlyBookedDescription'),
          },
          {
            checked: enableReEntry,
            onChange: () => setEnableReEntry(!enableReEntry),
            label: getLabelValue('AllowReentry'),
            description: getLabelValue('AllowReentryDescription'),
          },
          {
            checked: extendedParticipation,
            onChange: () => setExtendedParticipation(!extendedParticipation),
            label: 'Utökat deltagande',
            description: 'Du kan inkludera flera inpasserings registreringar vid inpassering av en person',
          },
          {
            checked: direction === 'In',
            onChange: () => setDirection(direction === 'In' ? 'Out' : 'In'),
            label: getLabelValue('PassageDirection'),
            description: getLabelValue('PassageDirectionDescription'),
          },
          {
            checked: alwaysPrint,
            onChange: setAlwaysPrint,
            label: getLabelValue('AlwaysPrintOnScan'),
            description: getLabelValue('AlwaysPrintOnScanDescription'),
          },
        ].map((setting, idx) => (
          <div key={idx} className="grid gap-2">
            <div className="flex flex-row items-start space-x-3 p-4 bg-white border rounded-md text-sm w-full">
              <Switch checked={setting.checked} onCheckedChange={setting.onChange} />
              <div className="flex-1 min-w-0 space-y-1 leading-none">
                <Label className="font-semibold break-words">{setting.label}</Label>
                <p className="break-words text-sm text-muted-foreground">{setting.description}</p>
              </div>
            </div>
          </div>
        ))}

        {/* User Card */}
        <Card className="w-full space-y-2">
          <CardContent className="flex flex-col gap-2 p-4">
            <span className="text-sm text-muted-foreground">{getLabelValue('LoggedInAs')}</span>
            <div className="font-semibold text-md break-words">{user?.userName}</div>
            <div className="text-sm italic break-words">
              {getLabelValue('UnitId')} <code className="font-bold">{deviceId}</code>
            </div>

            <Separator />
            <Button variant="outline" className="w-full mt-4 text-destructive" onClick={logout}>
              {getLabelValue('Logout')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
